/**
 * Where the homepage hero is allowed to break, and where it is not.
 *
 * The hero headline is EDITABLE COPY (lib/hero-markup.ts, `/hero` in Slack), so
 * the break points cannot be hand-placed <br>s tuned to one wording — that is
 * exactly what this repo removed when the copy moved into `site_content`. What
 * replaced them, `text-wrap: balance`, makes the lines EVEN, which is a
 * different goal from making them READ well, and the two disagree in a way the
 * homepage kept losing:
 *
 *     Build your app. Get        Build apps and
 *     customers.                 get customers by
 *                                chatting with AI.
 *
 * Both are near-perfectly balanced. Both strand a word that belongs to the next
 * phrase — "Get", "and", "by" — so the reader hits a line end mid-thought and
 * has to carry the connector down to the following line. (The left-hand one is
 * plain greedy wrapping; balance is skipped or unsupported in some engines, so
 * the fix has to hold under BOTH line-breaking algorithms, not just balance.)
 *
 * The rule this module encodes is the typographic one: a connector belongs to
 * what FOLLOWS it, so the line may not break after it. That is expressed as
 * unbreakable runs — the caller renders each run so the browser cannot split
 * it — and it is copy-agnostic: it reads the words, not a specific headline.
 *
 * A LADDER OF WIDTHS, because "unbreakable" is a promise the narrow end of the
 * range cannot always keep. `white-space: nowrap` defeats the h1's
 * `break-words` floor: inside a nowrap run, `overflow-wrap` has no line to
 * break, so a run wider than the viewport does not wrap — it pushes the page
 * sideways, the one thing playwright/tests/responsive/homepage.spec.ts refuses
 * to allow at 360px. So each phrase is held from the narrowest width that can
 * actually fit it and left breakable below that, which is why the plan is a
 * TREE: "and get customers" holds from 412px up, and the "get customers" inside
 * it holds all the way down to 360px, where the whole phrase would not fit.
 *
 * Some copy runs out of ladder. On a 360px screen "and get customers" is 352px
 * of type in a 312px box, and no arrangement of it avoids a line ending on
 * "and" — the planner spends that break where it costs least (see packFromEnd)
 * rather than pretending it can be avoided.
 *
 * Pure and deterministic on purpose: the hero is server-rendered in
 * app/page.tsx and hydrated in app/home-client.tsx, so the same copy has to
 * plan to the same DOM on both sides or React reports a hydration mismatch.
 */
import type { HeroLine, HeroSpan } from "./hero-markup";

/**
 * Per-character advances, in em, for the hero's face (Geist 800, tracking
 * -0.02em), measured off the rendered page rather than assumed:
 *
 *   "and get customers"  352px @ 40px = 8.80em      "your app."   173px = 4.33em
 *   "Get customers."     287px @ 40px = 7.18em      "with AI."    137px = 3.43em
 *   "MAKE MONEY NOW"     367px @ 40px = 9.18em
 *
 * A run's width is estimated from these rather than from its character count,
 * because character count is wrong in the direction that hurts: the caps run
 * above is the same 14 characters as "Get customers." and 28% wider. A budget
 * that fits one overflows the other, and the hero is edited from Slack by
 * people who type in caps.
 *
 * The table deliberately rounds every class UP — it over-estimates ordinary
 * mixed-case copy by 5-10% — so the error always lands on the side of allowing
 * one more break, never on the side of a run that cannot fit its line. The
 * measurements above are pinned in tests/unit/lib/hero-wrapping.test.ts, which
 * fails if a change to these numbers ever makes the estimate come in UNDER a
 * real width.
 */
const ADVANCE_EM = {
  upper: 0.75,
  lower: 0.56,
  space: 0.26,
  punctuation: 0.28,
  other: 0.6,
} as const;

/** Estimated width of `text` in em, at whatever size the hero is rendering. */
export function advanceEm(text: string): number {
  let total = 0;
  for (const char of text) {
    if (char === " ") total += ADVANCE_EM.space;
    else if (/[a-z]/.test(char)) total += ADVANCE_EM.lower;
    else if (/[A-Z0-9]/.test(char)) total += ADVANCE_EM.upper;
    else if (/[.,!?:;'"’”\-–—()]/.test(char)) total += ADVANCE_EM.punctuation;
    else total += ADVANCE_EM.other;
  }
  return total;
}

/**
 * The ladder: how wide a run may be to be held together, and the class that
 * holds it, from the narrowest viewport up. Each budget IS the line it has to
 * fit — no safety factor is subtracted, because the estimator above already
 * carries one, and a second cut on top of it would refuse to hold
 * "Get customers." (7.4em estimated, 287px measured, on a line that holds
 * 312px), which is the phrase this whole change is about.
 *
 * A line at width `w` holds `(w - 48px gutters) / clamp(40px, 7vw, 64px)` em:
 *
 *   360px  narrowest width the responsive suite runs; font at its 40px floor.
 *          312/40 = 7.8em. Held with no variant at all — this tier is the one
 *          promise that has to hold everywhere.
 *   412px  the common large Android in the responsive matrix, and the first
 *          width where 352px of "and get customers" fits: 364/40 = 9.1em. This
 *          is the rung that buys that phrase back on a modern phone.
 *   640px  `sm`, where the font resumes scaling at 7vw = 44.8px: 592/44.8 =
 *          13.2em. That figure barely moves for the rest of the range (13.5em
 *          at 908px; 860/64 = 13.4em once both the box and the font cap), so
 *          one budget covers every width from `sm` up.
 *
 * The classes are written out in full because Tailwind scans source text for
 * them — a composed string would not survive the build.
 */
export interface HeroHoldTier {
  /** Tailwind class that holds this tier, or null for the always-held floor. */
  className: string | null;
  /** Widest run this tier may hold, in em. */
  maxEm: number;
}

export const HERO_HOLD_TIERS: readonly HeroHoldTier[] = [
  { className: null, maxEm: 7.8 },
  { className: "min-[412px]:whitespace-nowrap", maxEm: 9.1 },
  { className: "sm:whitespace-nowrap", maxEm: 13.0 },
];

/**
 * Words a line may not end on: the closed-class connectors, plus the short
 * imperative verbs marketing copy opens with, which read as incomplete without
 * their object ("Get" / "customers", "Build" / "your app").
 *
 * Listed explicitly rather than inferred from word length. "Get customers" and
 * "app. Get" both hinge on a three-letter word, and only the list knows that
 * the first is a phrase and the second is a sentence boundary. Lowercase; the
 * lookup folds case and ignores wrapping quotes.
 */
export const GLUE_AFTER_WORDS: ReadonlySet<string> = new Set([
  // Determiners
  "a",
  "an",
  "the",
  "this",
  "that",
  "these",
  "those",
  "any",
  "every",
  "my",
  "our",
  "your",
  "their",
  "its",
  // Prepositions
  "at",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "onto",
  "over",
  "per",
  "to",
  "up",
  "via",
  "with",
  "without",
  // Conjunctions
  "and",
  "as",
  "but",
  "if",
  "nor",
  "or",
  "so",
  "than",
  "then",
  "when",
  "while",
  "yet",
  // Light verbs / auxiliaries that carry no meaning until the next word does
  "are",
  "be",
  "can",
  "do",
  "does",
  "has",
  "have",
  "is",
  "it",
  "let",
  "no",
  "not",
  "we",
  "what",
  "why",
  "will",
  "you",
  // Imperative openers, the ones a hero headline actually starts with
  "build",
  "chat",
  "get",
  "go",
  "grow",
  "launch",
  "make",
  "meet",
  "sell",
  "ship",
  "start",
  "try",
  "turn",
  "use",
  "win",
]);

/** Punctuation that ENDS a thought — breaking after it is the intended break. */
const CLAUSE_END = /[.,!?:;…—–]$/;
/** Quotes and brackets are transparent: `app."` still ends its sentence. */
const TRAILING_MARKS = /["'”’)\]]+$/;
const LEADING_MARKS = /^["'“‘(\[]+/;

/** True when a line may not break after this word. */
export function gluesForward(word: string): boolean {
  const bare = word.replace(TRAILING_MARKS, "");
  if (CLAUSE_END.test(bare)) return false;
  return GLUE_AFTER_WORDS.has(bare.replace(LEADING_MARKS, "").toLowerCase());
}

/**
 * A planned piece of a line.
 *
 * `run` is copy the browser may never break; `hold` is a group of pieces kept
 * on one line from a given width up, and its children are what it falls back to
 * below that width. A `hold` always has at least two children — a group of one
 * needs no element.
 */
export type HeroNode =
  | { kind: "run"; spans: HeroSpan[]; hold: boolean }
  | { kind: "hold"; className: string; children: HeroNode[] };

interface Word {
  text: string;
  start: number;
  end: number;
}

/** Words with their offsets into the line's concatenated text. */
function splitWords(text: string): Word[] {
  const found: Word[] = [];
  const pattern = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    found.push({
      text: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return found;
}

/**
 * Greedy packing, walked from the END of the phrase.
 *
 * Direction matters. "and get customers" does not fit a 360px line, and the two
 * ways to split it are not equal: from the left it yields "and get" + a lone
 * "customers", re-stranding the verb this module exists to protect; from the
 * right it yields a lone "and" + "get customers", which keeps the tightest bond
 * — the one nearest the end of the phrase — whole. That is where the
 * unavoidable break gets spent.
 *
 * Generic over what it is packing: words at the bottom of the ladder, then the
 * chunks the tier below produced. `width` is in em and additive, so a chunk's
 * width is its parts plus the spaces between them.
 */
function packFromEnd<T extends { width: number }>(
  items: T[],
  maxEm: number,
): T[][] {
  const chunks: T[][] = [];
  let current: T[] = [];
  let width = 0;

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    const grown =
      current.length === 0 ? item.width : width + ADVANCE_EM.space + item.width;
    if (current.length > 0 && grown > maxEm) {
      chunks.unshift(current);
      current = [item];
      width = item.width;
    } else {
      current.unshift(item);
      width = grown;
    }
  }
  if (current.length > 0) chunks.unshift(current);
  return chunks;
}

/** The painted spans covering [start, end) of the line's text. */
function sliceSpans(line: HeroLine, start: number, end: number): HeroSpan[] {
  const out: HeroSpan[] = [];
  let offset = 0;
  for (const span of line) {
    const spanEnd = offset + span.text.length;
    const from = Math.max(start, offset);
    const to = Math.min(end, spanEnd);
    if (to > from) {
      out.push({ ...span, text: span.text.slice(from - offset, to - offset) });
    }
    offset = spanEnd;
    if (offset >= end) break;
  }
  return out;
}

/**
 * Fold neighbours that are painted the same way, so a held run emits one <span>
 * per paint change rather than one per word. The separating space is emitted
 * unpainted and only folds into an unpainted neighbour — a space swallowed into
 * an accent run would stretch that run's `bg-clip-text` gradient box.
 */
function foldSpans(spans: HeroSpan[]): HeroSpan[] {
  const out: HeroSpan[] = [];
  for (const span of spans) {
    const last = out[out.length - 1];
    if (last && last.accent === span.accent && last.bold === span.bold) {
      out[out.length - 1] = { ...last, text: last.text + span.text };
    } else {
      out.push({ ...span });
    }
  }
  return out;
}

/** A planned node and the width it occupies, while the tree is being built. */
interface Sized {
  node: HeroNode;
  width: number;
}

/** Rejoin words with a single space, carrying each word's paint across. */
function toRun(line: HeroLine, words: (Word & { width: number })[]): Sized {
  const parts: HeroSpan[] = [];
  words.forEach((word, index) => {
    if (index > 0) parts.push({ text: " ", accent: false, bold: false });
    parts.push(...sliceSpans(line, word.start, word.end));
  });
  const spans = foldSpans(parts);
  return {
    node: { kind: "run", spans, hold: words.length > 1 },
    width: advanceEm(spans.map((span) => span.text).join("")),
  };
}

/** Join what one tier packed together, or hand a lone chunk straight back. */
function toHold(chunk: Sized[], className: string | null): Sized {
  const width = chunk.reduce(
    (total, item, index) =>
      total + item.width + (index > 0 ? ADVANCE_EM.space : 0),
    0,
  );
  // A tier with nothing to hold, or no class to hold it with, adds no element.
  if (chunk.length === 1 || className === null) {
    return chunk.length === 1 ? chunk[0] : { node: chunk[0].node, width };
  }
  return {
    node: { kind: "hold", className, children: chunk.map((item) => item.node) },
    width,
  };
}

/**
 * Plan one line of parsed hero markup into nodes the renderer joins with an
 * ordinary, breakable space.
 *
 * Runs of internal whitespace collapse to a single space: the copy is a
 * headline typed into a Slack modal, and a double space there is a typo whose
 * only effect on the page would be to widen a line for no reason.
 */
export function planHeroLine(line: HeroLine): HeroNode[] {
  const words = splitWords(line.map((span) => span.text).join("")).map(
    (word) => ({ ...word, width: advanceEm(word.text) }),
  );
  if (words.length === 0) return [];

  // Maximal stretches of words that no break may fall inside.
  const phrases: (typeof words)[] = [];
  let phrase: typeof words = [words[0]];
  for (let i = 1; i < words.length; i++) {
    if (gluesForward(words[i - 1].text)) {
      phrase.push(words[i]);
    } else {
      phrases.push(phrase);
      phrase = [words[i]];
    }
  }
  phrases.push(phrase);

  return phrases.flatMap((phraseWords) => {
    // Climb the ladder: pack into runs that fit the narrowest line, then wrap
    // those in each wider tier that can hold them. Whatever is still too wide
    // for the top tier comes back as siblings, free to break between them.
    const [floor, ...tiers] = HERO_HOLD_TIERS;
    let level: Sized[] = packFromEnd(phraseWords, floor.maxEm).map((chunk) =>
      toRun(line, chunk),
    );
    for (const tier of tiers) {
      level = packFromEnd(level, tier.maxEm).map((chunk) =>
        toHold(chunk, tier.className),
      );
    }
    return level.map((item) => item.node);
  });
}

/** The visible text of a node — what the browser is being asked to keep whole. */
export function heroNodeText(node: HeroNode): string {
  return node.kind === "run"
    ? node.spans.map((span) => span.text).join("")
    : node.children.map(heroNodeText).join(" ");
}
